import React from "react";
import { Skeleton, Box, SxProps, Theme } from "@mui/material";

/**
 * Generic Skeleton Loaders
 * Reusable skeleton components for loading states
 * Based on Component Reusability Phase 3 specification
 */

// ============================================================
// 1. IconSkeleton - Circular/rounded icons
// ============================================================
interface IconSkeletonProps {
  size?: number;
  variant?: "circular" | "rounded";
  sx?: SxProps<Theme>;
}

export const IconSkeleton: React.FC<IconSkeletonProps> = ({
  size = 40,
  variant = "circular",
  sx,
}) => (
  <Skeleton
    variant={variant}
    width={size}
    height={size}
    sx={{ bgcolor: "rgba(255,255,255,0.2)", ...sx }}
  />
);

// ============================================================
// 2. TextSkeleton - Text lines with custom width/fontSize
// ============================================================
interface TextSkeletonProps {
  width?: string | number;
  fontSize?: string;
  sx?: SxProps<Theme>;
}

export const TextSkeleton: React.FC<TextSkeletonProps> = ({
  width = "100%",
  fontSize,
  sx,
}) => (
  <Skeleton
    variant="text"
    width={width}
    sx={{ fontSize: fontSize || "1rem", ...sx }}
  />
);

// ============================================================
// 3. MetricSkeleton - Large metric numbers (3rem)
// ============================================================
interface MetricSkeletonProps {
  sx?: SxProps<Theme>;
}

export const MetricSkeleton: React.FC<MetricSkeletonProps> = ({ sx }) => (
  <Box sx={sx}>
    <Skeleton variant="text" width="60%" sx={{ fontSize: "3rem" }} />
    <Skeleton variant="text" width="40%" height={16} sx={{ mt: 1 }} />
  </Box>
);

// ============================================================
// 4. CardSkeleton - Full card structure (icon + metric + label)
// ============================================================
interface CardSkeletonProps {
  iconSize?: number;
  sx?: SxProps<Theme>;
}

export const CardSkeleton: React.FC<CardSkeletonProps> = ({
  iconSize = 56,
  sx,
}) => (
  <Box sx={{ p: 3, ...sx }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 2,
      }}
    >
      <IconSkeleton size={iconSize} variant="rounded" />
      <Skeleton variant="text" width={60} sx={{ fontSize: "1.25rem" }} />
    </Box>
    <Skeleton variant="text" sx={{ fontSize: "3rem", mb: 1 }} />
    <Skeleton variant="text" sx={{ fontSize: "0.875rem" }} width="80%" />
  </Box>
);

// ============================================================
// 5. RowSkeleton - Flex row with multiple columns
// ============================================================
interface RowSkeletonProps {
  columns?: number;
  sx?: SxProps<Theme>;
}

export const RowSkeleton: React.FC<RowSkeletonProps> = ({
  columns = 4,
  sx,
}) => (
  <Box sx={{ display: "flex", gap: 2, alignItems: "center", py: 1, ...sx }}>
    {Array.from({ length: columns }).map((_, index) => (
      <Skeleton key={index} variant="text" width="100%" height={20} />
    ))}
  </Box>
);

// ============================================================
// 6. HeaderSkeleton - Icon + title + subtitle combo
// ============================================================
interface HeaderSkeletonProps {
  iconSize?: number;
  titleWidth?: string | number;
  showSubtitle?: boolean;
  sx?: SxProps<Theme>;
}

export const HeaderSkeleton: React.FC<HeaderSkeletonProps> = ({
  iconSize = 40,
  titleWidth = "40%",
  showSubtitle = true,
  sx,
}) => (
  <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2, ...sx }}>
    <IconSkeleton size={iconSize} variant="rounded" />
    <Box sx={{ flex: 1 }}>
      <Skeleton variant="text" width={titleWidth} height={36} />
      {showSubtitle && (
        <Skeleton variant="text" width="60%" height={20} sx={{ mt: 1 }} />
      )}
    </Box>
  </Box>
);

// ============================================================
// 7. ChartSkeleton - Chart with header and area
// ============================================================
interface ChartSkeletonProps {
  height?: number;
  showHeader?: boolean;
  showDropdown?: boolean;
  sx?: SxProps<Theme>;
}

export const ChartSkeleton: React.FC<ChartSkeletonProps> = ({
  height = 300,
  showHeader = false,
  showDropdown = false,
  sx,
}) => (
  <Box sx={sx}>
    {showHeader && (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Skeleton variant="text" width="30%" height={32} />
        {showDropdown && <Skeleton variant="rounded" width={120} height={40} />}
      </Box>
    )}
    <Skeleton
      variant="rectangular"
      width="100%"
      height={height}
      sx={{ borderRadius: 2 }}
    />
  </Box>
);

// ============================================================
// 8. TableSkeleton - Table rows with header
// ============================================================
interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  showAvatar?: boolean;
  textColumns?: number;
  chipColumns?: number;
  showIconHeader?: boolean;
  headerWidth?: number;
  sx?: SxProps<Theme>;
}

export const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rows = 5,
  columns = 4,
  showAvatar = false,
  textColumns = 3,
  chipColumns = 2,
  showIconHeader = false,
  headerWidth,
  sx,
}) => {
  // If custom column types are specified, calculate total columns
  const totalColumns = showAvatar
    ? 1 + textColumns + chipColumns
    : columns || textColumns + chipColumns;

  return (
    <Box sx={sx}>
      {/* Optional Icon Header (for cards with icon+title header) */}
      {showIconHeader && (
        <Box
          sx={{
            p: 3,
            pb: 2,
            borderBottom: "2px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <IconSkeleton size={48} variant="rounded" />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width={headerWidth || "40%"} height={32} />
            <Skeleton variant="text" width="60%" height={20} sx={{ mt: 0.5 }} />
          </Box>
        </Box>
      )}

      {/* Table Container */}
      <Box sx={{ p: 3 }}>
        {/* Table Header */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 2,
            pb: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          {showAvatar && <Skeleton variant="circular" width={40} height={40} />}
          {Array.from({ length: totalColumns - (showAvatar ? 1 : 0) }).map(
            (_, index) => (
              <Skeleton key={index} variant="text" width="100%" height={20} />
            ),
          )}
        </Box>

        {/* Table Rows */}
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <Box
            key={rowIndex}
            sx={{ display: "flex", gap: 2, mb: 1.5, alignItems: "center" }}
          >
            {/* Avatar column */}
            {showAvatar && (
              <Skeleton variant="circular" width={40} height={40} />
            )}

            {/* Text columns */}
            {Array.from({ length: textColumns }).map((_, colIndex) => (
              <Skeleton
                key={`text-${colIndex}`}
                variant="text"
                width="100%"
                height={20}
              />
            ))}

            {/* Chip columns */}
            {Array.from({ length: chipColumns }).map((_, colIndex) => (
              <Skeleton
                key={`chip-${colIndex}`}
                variant="rounded"
                width={80}
                height={24}
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ============================================================
// 9. ProgressBarSkeleton - Rating distribution bars
// ============================================================
interface ProgressBarSkeletonProps {
  sx?: SxProps<Theme>;
}

export const ProgressBarSkeleton: React.FC<ProgressBarSkeletonProps> = ({
  sx,
}) => (
  <Box sx={{ width: "100%", ...sx }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
      <Skeleton variant="text" width="30%" height={16} />
      <Skeleton variant="text" width="15%" height={16} />
    </Box>
    <Skeleton
      variant="rectangular"
      width="100%"
      height={8}
      sx={{ borderRadius: 1 }}
    />
  </Box>
);

// ============================================================
// 10. GradientCardSkeleton - Gradient background card skeleton
// ============================================================
interface GradientCardSkeletonProps {
  iconSize?: number;
  sx?: SxProps<Theme>;
}

export const GradientCardSkeleton: React.FC<GradientCardSkeletonProps> = ({
  iconSize = 48,
  sx,
}) => (
  <Box
    sx={{
      p: 3,
      borderRadius: 2,
      bgcolor: "rgba(255,255,255,0.1)",
      ...sx,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
      <IconSkeleton size={iconSize} />
      <Skeleton variant="text" width="50%" height={24} />
    </Box>
    <Skeleton variant="text" width="40%" sx={{ fontSize: "3rem" }} />
    <Skeleton variant="text" width="60%" height={16} sx={{ mt: 1 }} />
  </Box>
);
